<template>
    <ToastContainer />
    <InteractionSummaryHeader />
    <section class="interactive">
        <FilterBar
            ref="filterBarRef"
            :items="filters"
            @apply="applyFilters"
            page="interactions-summary"
            :title="route.meta.title || ''"
        />
        <hr class="filter-bar-hr">
        <div
            class="dashboard-content"
        >
            <div
                class="dashboard-content"
                v-if="filterStatus === FilterStatus.FILTER_COMPLETED"
            >
                <TotalWorkloadEffortChart
                    class="box effect1"
                    :categories="categories"
                    :series="totalWorkloadEffortSeries"
                />
                <CountWorkloadInteractionChart
                    class="box effect1"
                    :categories="categories"
                    :series="countWorkloadInteractionSeries"
                />
                <div class="box effect1">
                    <div class="tabs-header">
                        <div class="tabs-title">
                            <h1>Workstream Interactions</h1>
                        </div>
                    </div>
                    <div class="tabs-content">
                        <tabs>
                            <tab title="Workstream Interactions by Workloads">
                                <WorkstreamInteractionByWorkloadGrid
                                    :data="workstreamInteractionByWorkload"
                                    :workstreams="workstreamList"
                                    :workloads="workloadList"
                                />
                            </tab>
                            <tab title="Workstream Interactions by Types">
                                <WorkstreamInteractionByTypeGrid
                                    :data="workstreamInteractionByType"
                                    :workstreams="workstreamList"
                                    :workloads="workloadList"
                                />
                            </tab>
                        </tabs>
                    </div>
                </div>
                <div class="box effect1">
                    <div class="tabs-header">
                        <div class="tabs-title">
                            <h1>User Interactions</h1>
                        </div>
                    </div>
                    <div class="tabs-content">
                        <tabs>
                            <tab title="User Interactions by Workloads">
                                <UserInteractionByWorkloadGrid
                                    :data="userInteractionByWorkload"
                                    :teams="teamList"
                                    :workloads="workloadList"
                                />
                            </tab>
                            <tab title="User Interactions by Types">
                                <UserInteractionByTypeGrid
                                    :data="userInteractionByType"
                                    :teams="teamList"
                                    :workloads="workloadList"
                                />
                            </tab>
                        </tabs>
                    </div>
                </div>
                <div class="box effect1">
                    <WorkstreamInteractionByDateGrid
                        :data="workstreamInteractionByDate"
                        :workloads="workloadList"
                    />
                </div>
                <FocusEvents
                    v-if="isFocusEnabled"
                    :data="focusData"
                    :config="focusConfig"
                    :teams="teamList"
                    :users="userList"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
// eslint-disable max-lines
import { ref, onMounted } from 'vue';
import { LicenseManager } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';

import * as Toast from '@agyletime/vue-3-common/src/components/Toast/toastState';
import { ToastContainer } from '@agyletime/vue-3-common/src/components/Toast';
import {
    initialiseLocale,
    loadFilterDependencies
} from './loadFilterDependencies';
import { BaseModel } from '../../types';
import {
    getData,
    WorkstreamInteractionWorkstreamData,
    WorkstreamInteractionGroupOrTeamData,
    WorkstreamInteractionIntervalData,
    SeriesData,
    retrieveFocusConfig
} from './getData';
import { getQueryFilters, validateInteractionQuery } from './queryValidation';
import numberValue from '../../utils/numberValue';
import moment from 'moment';
import { tz } from 'moment-timezone';
import { useRoute } from 'vue-router';
import type { Team } from '@agyletime/vue-3-common/src/typings/team';

// Common Components
import Tabs from '../common/tabs/Tabs.vue';
import Tab from '../common/tabs/Tab.vue';
// Components
import InteractionSummaryHeader from '@/components/interactionSummaryHeader/InteractionSummaryHeader.vue';
import TotalWorkloadEffortChart from '@/components/totalWorkloadEffort/TotalWorkloadEffort.vue';
import CountWorkloadInteractionChart from '@/components/countWorkloadInteraction/CountWorkloadInteraction.vue';
import WorkstreamInteractionByWorkloadGrid from '@/components/workstreamInteractionByWorkload/WorkstreamInteractionByWorkload.vue';
import WorkstreamInteractionByTypeGrid from '@/components/workstreamInteractionByType/WorkstreamInteractionByType.vue';
import UserInteractionByWorkloadGrid from '@/components/userInteractionByWorkload/UserInteractionByWorkload.vue';
import UserInteractionByTypeGrid from '@/components/userInteractionByType/UserInteractionByType.vue';
import WorkstreamInteractionByDateGrid from '@/components/workstreamInteractionByDate/WorkstreamInteractionByDate.vue';
import FocusEvents from '../focusEvents/FocusEvents.vue';
import { FocusSummary } from '@/types/interactionSummary';

// Using mock data from json file
// import interactionSummary from '../../../interactionSummaryPayload.json';

/* eslint-disable-next-line max-len */
LicenseManager.setLicenseKey('CompanyName=Agyle Time,LicensedApplication=Agyle Time,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=12,LicensedProductionInstancesCount=1,AssetReference=AG-027189,ExpiryDate=28_May_2023_[v2]_MTY4NTIyODQwMDAwMA==adf5725b8da6512ae5a848d6fdf5c481');

const route = useRoute();

enum FilterStatus {
    INITIAL = 'initial',
    FILTER_INITIATED = 'filter_initiated',
    FILTER_COMPLETED = 'filter_completed',
    FILTER_ERROR = 'filter_error',
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filters = ref<any>([]);
const filterStatus = ref<FilterStatus>(FilterStatus.INITIAL);
const workstreamInteractionByWorkload =
    ref<WorkstreamInteractionWorkstreamData>({});
const workstreamInteractionByType = ref<WorkstreamInteractionWorkstreamData>(
    {}
);
const userInteractionByWorkload = ref<WorkstreamInteractionGroupOrTeamData[]>(
    []
);
const userInteractionByType = ref<WorkstreamInteractionGroupOrTeamData[]>([]);
const workstreamInteractionByDate = ref<WorkstreamInteractionIntervalData>({});

const categories = ref<string[]>([]);
const totalWorkloadEffortSeries = ref<SeriesData[]>([]);
const countWorkloadInteractionSeries = ref<SeriesData[]>([]);
const userDefaultTimezone: string = tz.guess();
const teamList = ref<Team[]>([]);
const workstreamList = ref<BaseModel[]>([]);
const workloadList = ref<BaseModel[]>([]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const userList = ref<any[]>([]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const focusConfig = ref<any>({});
const isFocusEnabled = ref<boolean>(false);
const focusData = ref<FocusSummary[]>([]);
// eslint-disable-next-line @typescript-eslint/no-explicit-any, complexity
const applyFilters = async (filter: Record<string, any>) => {
    // Handle double trigger of Apply button
    if (filterStatus.value === FilterStatus.FILTER_INITIATED) {
        Toast.addToast(
            'filter_already_initiated',
            'Please wait while we retrieve your data',
            'ERROR'
        );
        return;
    }

    if (!filter) return;

    // Dehyrate filters into known query params
    const queryFilters = getQueryFilters(filter);

    // Query validation
    const validationResult = validateInteractionQuery(queryFilters);
    if (validationResult.error) {
        Toast.addToast(
            'filter_validation_error',
            validationResult.error.message,
            'ERROR'
        );
        return;
    }
    filterStatus.value = FilterStatus.FILTER_INITIATED;

    try {
        workstreamInteractionByWorkload.value = {};
        workstreamInteractionByType.value = {};
        userInteractionByWorkload.value = [];
        userInteractionByType.value = [];
        workstreamInteractionByDate.value = {};
        totalWorkloadEffortSeries.value = [];
        countWorkloadInteractionSeries.value = [];
        // Get Interaction Data here
        const interactionData = await getData(queryFilters, isFocusEnabled.value, teamList.value, userList.value);

        filterStatus.value = FilterStatus.FILTER_COMPLETED;

        // Check
        let isEmptyData = true;
        const dataEmptyStatus: {
            workstreamDataByWorkload: boolean;
            workstreamDataByType: boolean;
            teamDataByWorkload: boolean;
            teamDataByType: boolean;
        intervalData: boolean;
        focusData: boolean;
        } = {
            workstreamDataByWorkload: false,
            workstreamDataByType: false,
            teamDataByWorkload: false,
            teamDataByType: false,
            intervalData: false,
            focusData: false
        };
        if (!Object.keys(interactionData.workstreamData.byWorkload).length) {
            dataEmptyStatus.workstreamDataByWorkload = true;
        }
        if (!Object.keys(interactionData.workstreamData.byType).length) {
            dataEmptyStatus.workstreamDataByType = true;
        }
        if (!interactionData.teamData.byWorkload.length)
            dataEmptyStatus.teamDataByWorkload = true;
        if (!interactionData.teamData.byType.length)
            dataEmptyStatus.teamDataByType = true;
        if (!Object.keys(interactionData.intervalData).length)
            dataEmptyStatus.intervalData = true;
        if (!interactionData.focusData?.length)
            dataEmptyStatus.focusData = true;
        // Check if all is Empty
        Object.keys(dataEmptyStatus).forEach((dKey) => {
            if (!dataEmptyStatus[dKey as keyof typeof dataEmptyStatus]) isEmptyData = false;
        });

        if (isEmptyData) {
            Toast.addToast(
                'data_empty',
                'There is no data available for the selected filters.',
                'INFO'
            );
            filterStatus.value = FilterStatus.INITIAL;
            return;
        }
        workstreamInteractionByWorkload.value =
            interactionData.workstreamData.byWorkload;
        workstreamInteractionByType.value =
            interactionData.workstreamData.byType;
        userInteractionByWorkload.value = interactionData.teamData.byWorkload;
        userInteractionByType.value = interactionData.teamData.byType;
        workstreamInteractionByDate.value = interactionData.intervalData;

        const interactions = Object.entries(interactionData.intervalData).sort(
            (a, b) => Number.parseInt(b[0]) - Number.parseInt(a[0])
        );
        const dates: string[] = [];
        const allTWESeries: SeriesData[] = [];
        const allCWISeries: SeriesData[] = [];

        if (interactions.length) {
            interactions[0][1].forEach((data) => {
                dates.push(moment(data.date).format('DD MMM'));
            });
        }

        interactions.forEach(([ key, value ], index) => {
            let dataKey = 'N/A';
            const nKey = Number.parseInt(key + '');
            if (!Number.isNaN(nKey)) {
                const foundWorkload = workloadList.value.find(
                    (workload) => workload.id === nKey
                );
                if (foundWorkload) {
                    dataKey = foundWorkload.name;
                }
            }

            const tweSeries: SeriesData = {
                name: dataKey,
                data: value.map((data) => numberValue(data.totalHandleTime)),
                visible: true
            };
            const cwiSeries: SeriesData = {
                name: dataKey,
                data: value.map((data) => data.interactions),
                visible: true
            };

            if (index >= allTWESeries.length) {
                allTWESeries.push(tweSeries);
            } else {
                allTWESeries[index] = tweSeries;
            }

            if (index >= allCWISeries.length) {
                allCWISeries.push(cwiSeries);
            } else {
                allCWISeries[index] = cwiSeries;
            }
        });

        categories.value = dates;
        totalWorkloadEffortSeries.value = allTWESeries;
        countWorkloadInteractionSeries.value = allCWISeries;
        // @ts-expect-error - need to look at this
        focusData.value = interactionData.focusData;
    } catch (error) {
        filterStatus.value = FilterStatus.FILTER_ERROR;
        Toast.addToast(
            'filter_error',
            (error as Error).message || 'There was an error retrieving your data.',
            'ERROR'
        );
    }
};

onMounted(async () => {
    const myOrg = await initialiseLocale();
    const {
        teams,
        users,
        workstreams,
        roles,
        timezones,
        allTeams,
        allWorkstreams,
        allWorkloads
    } = await loadFilterDependencies({
    // for drop down options
        workstreams: { queryParams: 'isDeleted=false' }
    });
    const focusSettings = await retrieveFocusConfig();
    if (focusSettings?.enabled) {
        isFocusEnabled.value = true;
        focusConfig.value = focusSettings.configuration;
    }
    teamList.value = allTeams;
    workstreamList.value = allWorkstreams;
    workloadList.value = allWorkloads;
    userList.value = users;

    filters.value = [
        {
            type: 'date',
            key: 'date',
            label: 'Date',
            weekStart: myOrg?.startWeekOn || 1,
            default: {
                range: [
                    moment().tz(userDefaultTimezone).startOf('week').toDate(),
                    moment().tz(userDefaultTimezone).endOf('week').toDate()
                ]
            }
        },
        {
            type: 'singleSelect',
            key: 'timezone',
            label: 'Timezone',
            nameKey: 'value',
            valueKey: 'value',
            options: timezones,
            // groupField: '',
            default: {
                name: userDefaultTimezone,
                value: userDefaultTimezone
            }
        },
        {
            type: 'multiSelect',
            key: 'teams',
            label: 'Team',
            nameKey: 'name',
            valueKey: 'id',
            options: teams,
            groupField: 'parentGroupName'
            // pluralLabel: ''
        },
        {
            type: 'multiSelect',
            key: 'users',
            label: 'User',
            /* eslint-disable-next-line max-lines */
            nameKey: 'name',
            valueKey: 'id',
            options: users.filter((user) => !user.endAt),
            groupField: 'teamName'
            // pluralLabel: ''
        },
        {
            type: 'multiSelect',
            key: 'workstreams',
            label: 'Workstream',
            nameKey: 'name',
            valueKey: 'id',
            options: workstreams
            // groupField: '',
            // pluralLabel: ''
        },
        {
            type: 'multiSelect',
            key: 'roles',
            label: 'Role',
            nameKey: 'name',
            valueKey: 'id',
            options: roles
            // groupField: '',
            // pluralLabel: ''
        },
        {
            type: 'input',
            key: 'tags',
            label: 'Tag'
        }
    ];
});
</script>

<style scoped lang="scss" src="./style.scss"></style>
